// store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

// Initial state
const initialState = {
  token: localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
  requiresVerification: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, remember }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { email, password, remember });
      if (response.data.success) {
        const { token, user, expires_in } = response.data.data;
        
        // Store token
        if (remember) {
          localStorage.setItem('authToken', token);
        } else {
          sessionStorage.setItem('authToken', token);
        }
        
        return { token, user, expires_in };
      }
      return rejectWithValue(response.data.message || 'Login failed');
    } catch (error) {
      // Handle verification required error
      if (error.response?.status === 403 && error.response?.data?.data?.requiresVerification) {
        return rejectWithValue({
          requiresVerification: true,
          message: error.response.data.message || 'Please verify your email address.',
          email: error.response.data.data.email,
        });
      }
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);
      if (response.data.success) {
        const { user, requiresVerification, emailSent } = response.data.data;
        // Don't store token - user must verify first
        return { user, requiresVerification, emailSent };
      }
      return rejectWithValue(response.data.message || 'Registration failed');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      if (token) {
        await api.post('/auth/logout');
      }
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return rejectWithValue(error.message);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      if (!token) return null;
      
      const response = await api.get('/auth/me');
      if (response.data.success) {
        const userData = response.data.data.user;
        if (localStorage.getItem('user')) {
          localStorage.setItem('user', JSON.stringify(userData));
        } else if (sessionStorage.getItem('user')) {
          sessionStorage.setItem('user', JSON.stringify(userData));
        }
        return userData;
      }
      return rejectWithValue(response.data.message || 'Failed to get profile');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get profile');
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/verify/${token}`);
      if (response.data.success) {
        return response.data;
      }
      return rejectWithValue(response.data.message || 'Verification failed');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Verification failed');
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (email, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      if (response.data.success) {
        return response.data;
      }
      return rejectWithValue(response.data.message || 'Failed to resend verification');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to resend verification');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.isInitialized = true;
      state.requiresVerification = false;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      const storage = localStorage.getItem('user') ? localStorage : sessionStorage;
      if (storage) {
        const currentUser = JSON.parse(storage.getItem('user') || '{}');
        storage.setItem('user', JSON.stringify({ ...currentUser, ...action.payload }));
      }
    },
    clearVerification: (state) => {
      state.requiresVerification = false;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.isInitialized = true;
        state.error = null;
        state.requiresVerification = false;
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        if (action.payload?.requiresVerification) {
          state.requiresVerification = true;
          state.error = action.payload.message;
        } else {
          state.error = action.payload || 'Login failed';
        }
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.token = null; // No token until verified
        state.user = action.payload.user;
        state.requiresVerification = action.payload.requiresVerification;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.error = action.payload || 'Registration failed';
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.isInitialized = true;
        state.requiresVerification = false;
      })
      .addCase(logout.rejected, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isInitialized = true;
        state.requiresVerification = false;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to get profile';
        state.isInitialized = true;
        if (action.payload === 'Unauthorized') {
          state.token = null;
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.requiresVerification = false;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Verification failed';
      })
      // Resend Verification
      .addCase(resendVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendVerification.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to resend verification';
      });
  },
});

// Export actions
export const { clearError, resetAuth, setInitialized, updateUser, clearVerification } = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectToken = (state) => state.auth.token;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectRequiresVerification = (state) => state.auth.requiresVerification;

export default authSlice.reducer;